/**
 * 指令管理器的公共类型：请求、结果、门禁、队列、历史、事件。
 *
 * 这一层只描述"界面需要知道的东西"，不含任何实现。分开的理由很实际：
 * 界面层要基于这些类型开发，而它们比 {@link './manager'} 稳定得多 ——
 * 重试策略、去重键、串行调度都会改，`CommandResult` 的字段不该跟着一起改。
 */

import type { CommandMeta, CommandRef, CommandRisk } from '@hgc/ardupilot';
import type { MavlinkSession } from '@hgc/link';

import type { EventBus } from '../events';
import type { VehicleEvents, VehicleState } from '../vehicle';
import type { CommandStatus } from './results';

/* ────────────────────────────── 车辆依赖 ────────────────────────────── */

/**
 * 管理器对"一辆车"的全部需求。
 *
 * 刻意不写 `Vehicle` 而是写这个结构类型：一来它把依赖面钉死在 5 个成员上
 * （改 `Vehicle` 时不必回头看管理器用了哪些隐式假设），二来测试可以只造这 5 样。
 *
 * 注意这里**没有** `sendCommandLong()`：会话层那个方法把 `confirmation` 固定填 0，
 * 而重传语义要求每次重发递增（见 {@link './manager'} 顶部）。管理器必须自己组帧，
 * 因此要的是 `session` 本身。
 */
export interface CommandVehicle {
  readonly systemId: number;
  readonly componentId: number;
  /** 当前状态快照（门禁判断的唯一来源） */
  readonly state: VehicleState;
  /** 车辆事件总线：管理器订阅 `commandAck` 与 `frame` */
  readonly events: EventBus<VehicleEvents>;
  readonly session: MavlinkSession;
}

/** 定时器注入点。测试用手动时钟替换掉，避免 `setTimeout` 让断言变得非确定。 */
export interface CommandTimers {
  set(handler: () => void, ms: number): number;
  clear(handle: number): void;
}

export const DEFAULT_COMMAND_TIMERS: CommandTimers = {
  // Node 的 setTimeout 返回 Timeout 对象，浏览器返回 number；对外统一成 number 句柄，
  // 转换只在这两处发生，免得调用方被迫处理两种类型
  set: (handler, ms) => setTimeout(handler, ms) as unknown as number,
  clear: (handle) => clearTimeout(handle as unknown as ReturnType<typeof setTimeout>),
};

/* ────────────────────────────── 请求 ────────────────────────────── */

/** 高度基准。`COMMAND_INT` 用 `MAV_FRAME` 表达它，`COMMAND_LONG` 只能靠命令自己的约定。 */
export type AltitudeFrame = 'relative' | 'absolute' | 'terrain';

export interface CommandRequest {
  /** 命令编号或名字（`MAV_CMD_NAV_TAKEOFF` / `NAV_TAKEOFF`，大小写不敏感） */
  readonly command: CommandRef;
  /**
   * `param1`…`param7`，**MAVLink 命令自己的单位**（经纬度是十进制度，不是 ×1e7 整数）。
   *
   * 未给出的位按协议约定填 0；显式传 `NaN` 会原样下发 —— 在 MAVLink 里
   * `NaN` 是"用当前值"的合法写法（例如 `NAV_TAKEOFF` 不指定坐标就先原地起飞），
   * 所以这里既不能把它当 0，也不能当成错误。
   */
  readonly params?: readonly (number | undefined)[];
  /**
   * 危险命令的二次确认。
   *
   * 界面上"用户点了确认框"之后才置 `true`。管理器**不弹窗**（它不认识界面），
   * 只负责在没确认时拦下来并说明原因。
   */
  readonly confirmed?: boolean;
  /** 命令带高度时的基准，默认 `relative`（相对返航点）—— 引导类命令的常规语义 */
  readonly altitudeFrame?: AltitudeFrame;
  /** 单次等待确认的时长，默认取管理器配置（3 秒） */
  readonly timeoutMs?: number;
  /** 重传上限，默认取管理器配置（3 次） */
  readonly maxRetries?: number;
  /**
   * 强制走 `COMMAND_LONG`（即使命令带经纬度）。
   *
   * 存在的理由是排障：怀疑"INT 与 LONG 的行为不一致"时需要能手动选一种再对照。
   * 常规路径不要用它 —— 精度会退化到米级。
   */
  readonly forceLong?: boolean;
}

/* ────────────────────────────── 门禁 ────────────────────────────── */

export type CommandBlockerCode =
  /** 编号不在上游 `MAV_CMD` 表里 */
  | 'unknown-command'
  /** 链路没打开或没连上 */
  | 'link-offline'
  /** 这个组件的心跳丢了 */
  | 'vehicle-offline'
  /** 机型不支持（或机型未知，无从判断） */
  | 'vehicle-unsupported'
  /** 只在解锁后有效的命令，当前未解锁 */
  | 'not-armed'
  /** 只在未解锁时允许的命令，当前已解锁 */
  | 'armed'
  /** 命令需要飞控已有位置估计，但没有 */
  | 'no-position'
  /** 危险命令缺二次确认 */
  | 'needs-confirmation'
  /** 同编号的命令正在飞（ACK 无法归属） */
  | 'command-busy'
  /** 管理器已销毁 */
  | 'disposed';

/** "为什么现在不能执行"的一条原因。界面直接拿去显示，不需要再加工。 */
export interface CommandBlocker {
  readonly code: CommandBlockerCode;
  /** 面向飞手的中文原因 */
  readonly text: string;
}

/**
 * 门禁结论。
 *
 * `allowed === false` 时 `blockers` 非空；`allowed === true` 时仍可能有 `warnings`
 * —— 例如"能发，但坐标会以 float32 下发，精度退化到米级"。这两者必须分开：
 * 把警告做成拦截会让用户在很多正常场景下无法操作，把拦截降级成警告则会让
 * "未解锁就起飞"这种命令发出去。
 */
export interface CommandGateResult {
  readonly allowed: boolean;
  readonly command: number | null;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly meta: CommandMeta | null;
  readonly risk: CommandRisk | null;
  /** 是否需要界面二次确认（危险命令） */
  readonly needsConfirmation: boolean;
  /** 这次下发是否会走 `COMMAND_INT` */
  readonly viaInt: boolean;
  readonly blockers: readonly CommandBlocker[];
  readonly warnings: readonly string[];
}

/* ────────────────────────────── 结果 ────────────────────────────── */

/**
 * 飞控在失败前后用 `STATUSTEXT` 单独播报的原因。
 *
 * 为什么需要它：`COMMAND_ACK.result` 只能表达"失败了"，**说不了为什么**。
 * 具体原因常常只在同一时刻的一条状态文本里，而且那条文本在飞控内部是
 * "同一个处理函数里先发文本、后发 ACK" —— 换句话说它在线上通常**早于** ACK 到达。
 * 一个已核实的例子（ArduCopter 直升机模式的油门 runup 检查）：
 *
 * ```cpp
 * if (!ignore_checks && !new_flightmode->has_manual_throttle() &&
 *     motors->get_spool_state() != THROTTLE_UNLIMITED) {
 *     mode_change_failed(new_flightmode, "runup not complete"); // → STATUSTEXT
 *     return false;                                            // → COMMAND_ACK FAILED
 * }
 * ```
 *
 * `get_spool_state()` 是 `AP_Motors` 的内部状态，不出现在任何遥测消息里，
 * 所以地面站**无法**预先判断这类拒绝，只能靠这条文本把原因还原出来。
 */
export interface CommandReason {
  /** 状态文本原文（飞控给的英文或中文，不翻译） */
  readonly text: string;
  /** `MAV_SEVERITY` 原始值 */
  readonly severity: number;
  readonly severityName: string | null;
  readonly at: number;
}

export interface CommandResult {
  /** 本条命令的内部序号（同一管理器内递增，供事件与历史对齐） */
  readonly id: number;
  /** 命令编号；引用无法解析成编号时为 `null` */
  readonly command: number | null;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly risk: CommandRisk | null;
  /** 实际下发的参数（补齐到 7 位后的快照） */
  readonly params: readonly number[];
  readonly status: CommandStatus;
  /** 只有 `accepted` 为 `true`。界面判断"成功/失败"只看这一个字段 */
  readonly ok: boolean;
  /** 面向飞手的中文结论 */
  readonly text: string;
  /**
   * 关联到的飞控状态文本（失败原因）。
   *
   * 非空时 `text` 会写成"结论：原因"的形式（例如
   * `执行失败：Mode change to LOITER failed: runup not complete`），
   * 这里保留结构化的一份供界面做样式区分（按 `severity` 着色）。
   */
  readonly reasons: readonly CommandReason[];
  /** `MAV_RESULT` 原值；从未收到任何确认时为 `null` */
  readonly mavResult: number | null;
  readonly mavResultName: string | null;
  /** 实际发出的帧数（首次 + 重传）。0 表示被门禁拦下或发送失败 */
  readonly attempts: number;
  /** 最后收到的进度（0–100）；飞控没报过进度时为 `null` */
  readonly progress: number | null;
  readonly resultParam2: number | null;
  /** 是否以 `COMMAND_INT` 下发 */
  readonly viaInt: boolean;
  /** 从入队到有结论的耗时（含排队等待） */
  readonly durationMs: number;
  readonly at: number;
  /** 仅 `blocked` 时非空 */
  readonly blockers: readonly CommandBlocker[];
}

/* ────────────────────────────── 队列 ────────────────────────────── */

export type CommandQueueState =
  /** 排队中，还没发出去 */
  | 'queued'
  /** 正在飞：占据串行槽，等待确认 */
  | 'active'
  /** 已发出但已让出串行槽，仍在等确认（已知耗时的命令，见 `KNOWN_LONG_RUNNING`） */
  | 'awaiting-ack'
  /** 已收到 `IN_PROGRESS`：飞控正在执行长任务，等最终结果 */
  | 'long-running';

export interface CommandQueueEntry {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly risk: CommandRisk | null;
  readonly state: CommandQueueState;
  /** 已发出的帧数（重传会累加） */
  readonly attempts: number;
  /** 上一次发出时带的 `confirmation`（重传计数） */
  readonly confirmation: number;
  readonly enqueuedAt: number;
  /** 已经等了多久（排队 + 飞行） */
  readonly waitingMs: number;
  readonly viaInt: boolean;
}

/** 队列可见性快照。界面据此显示"当前在飞/排队中/长任务执行中"。 */
export interface CommandQueueSnapshot {
  /** 占据串行槽、正在等确认的命令（同时最多一条） */
  readonly active: CommandQueueEntry | null;
  readonly queued: readonly CommandQueueEntry[];
  /**
   * 已经发出去、但已让出串行槽的命令。
   *
   * 两种来源：飞控回了 `IN_PROGRESS` 的长任务（校准、返航），
   * 以及本地已知耗时的命令（`KNOWN_LONG_RUNNING`，飞控对它们通常只回
   * `ACCEPTED` 然后自己慢慢做）。它们不阻塞后续命令，但仍在等最终确认。
   */
  readonly awaitingAck: readonly CommandQueueEntry[];
}

/* ────────────────────────────── 历史 ────────────────────────────── */

/**
 * "刚才那条命令到底怎么了"—— 地面站最常被问的问题。
 *
 * 存的是**结论**（状态、文案、耗时、重传次数），不是原始帧：原始帧在 tlog 里，
 * 这里的价值是"不用翻日志就能回答"。
 */
export interface CommandHistoryEntry {
  readonly id: number;
  /** 命令编号；引用无法解析成编号时为 `null` */
  readonly command: number | null;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly risk: CommandRisk | null;
  readonly params: readonly number[];
  readonly status: CommandStatus;
  readonly ok: boolean;
  readonly text: string;
  /** 关联到的飞控状态文本（失败原因），见 {@link CommandReason} */
  readonly reasons: readonly CommandReason[];
  readonly mavResult: number | null;
  readonly attempts: number;
  readonly durationMs: number;
  readonly viaInt: boolean;
  readonly at: number;
}

/* ────────────────────────────── 事件 ────────────────────────────── */

export interface CommandSentEvent {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly params: readonly number[];
  /** 第几次发送（1 = 首次） */
  readonly attempt: number;
  /**
   * 这一帧带的 `confirmation`（重传计数）：首次 0，每次重发 +1。
   * 对端靠它区分"这是重传"与"用户又按了一次"。
   */
  readonly confirmation: number;
  readonly viaInt: boolean;
  readonly at: number;
}

export interface CommandAckedEvent {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly mavResult: number;
  readonly mavResultName: string | null;
  /** 这一帧确认的分类（`in-progress` 不是终态） */
  readonly status: CommandStatus;
  readonly text: string;
  readonly progress: number | null;
  readonly resultParam2: number;
  /** 从入队到收到这一帧确认的耗时（毫秒） */
  readonly durationMs: number;
  readonly at: number;
}

export interface CommandRetryingEvent {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  /** 即将发出的下一次发送序号 */
  readonly attempt: number;
  /** 即将发出的下一帧要带的 `confirmation` */
  readonly confirmation: number;
  readonly reason: 'timeout' | 'temporarily-rejected' | 'format-switch';
  readonly text: string;
  readonly at: number;
}

export interface CommandProgressEvent {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  /** 0–100；这一帧没有进度信息时为 `null` */
  readonly progress: number | null;
  readonly mavResult: number;
  readonly at: number;
}

export interface CommandFailedEvent {
  readonly id: number;
  readonly command: number;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly result: CommandResult;
  readonly at: number;
}

/** 门禁拦下（含危险命令缺二次确认）。帧没有发出去。 */
export interface CommandRejectedEvent {
  /** 本次尝试的序号（也会进历史）；无法解析命令时为 `null` */
  readonly id: number | null;
  readonly command: number | null;
  readonly commandName: string | null;
  readonly label: string | null;
  readonly blockers: readonly CommandBlocker[];
  /** 汇总后的中文原因（多条用"；"连接） */
  readonly text: string;
  readonly at: number;
}

/** 协议能力位变化（收到 `AUTOPILOT_VERSION`），界面据此开关高精度坐标等选项 */
export interface CommandCapabilitiesEvent {
  /** `MAV_PROTOCOL_CAPABILITY` 位域；未知时为 `null` */
  readonly capabilities: number | null;
  /** 是否具备 `COMMAND_INT` 能力 */
  readonly commandInt: boolean;
  readonly at: number;
}

export type CommandManagerEvents = {
  sent: CommandSentEvent;
  acked: CommandAckedEvent;
  retrying: CommandRetryingEvent;
  progress: CommandProgressEvent;
  failed: CommandFailedEvent;
  rejected: CommandRejectedEvent;
  queue: CommandQueueSnapshot;
  capabilities: CommandCapabilitiesEvent;
};

/* ────────────────────────────── 管理器配置 ────────────────────────────── */

export interface CommandManagerOptions {
  readonly vehicle: CommandVehicle;
  /** 时钟注入（测试用手动时钟） */
  readonly now?: () => number;
  /** 定时器注入（测试用手动时钟） */
  readonly timers?: CommandTimers;
  /** 单次等待确认的时长，默认 3000ms */
  readonly ackTimeoutMs?: number;
  /**
   * 已知耗时命令（`KNOWN_LONG_RUNNING`，例如各种校准）第一次等确认的时长，默认 10 秒。
   *
   * 比默认值长的唯一目的是**少重传**：校准类命令在真机上常常几秒才回第一帧确认，
   * 按 3 秒超时重传会在链路上留下一串无意义的重复帧。
   */
  readonly longCommandAckTimeoutMs?: number;
  /**
   * 长任务的绝对上限，默认 15 分钟。
   *
   * 收到 `IN_PROGRESS` 后不再按 `ackTimeoutMs` 判失败（校准本来就慢），
   * 但"永远等不到"也不行：这个上限保证管理器最终会给出一个结论，
   * 而不是让界面上那条进度条一直转。
   */
  readonly longTaskTimeoutMs?: number;
  /** 超时重传次数上限，默认 3（总发送次数最多 4 次） */
  readonly maxRetries?: number;
  /** 因 `TEMPORARILY_REJECTED` 重发的次数上限，默认 3 */
  readonly maxTemporaryRetries?: number;
  /**
   * 失败结论**之前**多久内的状态文本算这条命令的原因，默认 400ms。
   *
   * 为什么向前看：ArduPilot 是在**同一个处理函数里先发状态文本、再发 `COMMAND_ACK`** 的
   * （`mode_change_failed()` 之后才 return false → 上层再发 ACK），两帧在同一串发送
   * 队列里紧挨着，所以原因在线上通常**早于** ACK 到达。只向后看会漏掉最常见的形态。
   * 400ms 覆盖了两帧在 57600 波特链路上背靠背发出的时间差与抖动，又不至于把
   * "上一条命令的解释"拉进来。
   */
  readonly statustextLookbackMs?: number;
  /**
   * 失败结论**之后**继续收集状态文本的时长，默认 300ms（设 0 表示不做关联）。
   *
   * 为什么还需要向后看：文本与 ACK 之间可能隔着别的下行帧（遥测混流），
   * 或者固件把 ACK 先发、文本后发（ArduPilot 里两种顺序都存在）。
   * 300ms 在 1Hz～50Hz 的遥测流里最多夹进十几帧，而"下一条命令的解释"
   * 最早也要等下一次用户操作，不会落在这 300ms 里。
   */
  readonly statustextGraceMs?: number;
  /** 命令历史条数上限，默认 50 */
  readonly historyLimit?: number;
  /** 危险命令是否强制二次确认，默认 `true`。关掉它会同时失去 `rejected` 事件 */
  readonly enforceConfirmation?: boolean;
  /**
   * 协议能力位的来源。不指定时取自管理器自己从 `AUTOPILOT_VERSION` 帧里学到的值
   * （车辆状态将来把 `version.capabilities` 归集出来后，界面可以在这里接上去）。
   */
  readonly capabilities?: () => number | null;
  readonly onListenerError?: (error: unknown, type: string) => void;
}
